import { useIonToast } from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useState } from 'react';
import * as Yup from 'yup';

import LoadableContent from '../../components/content/LoadableContent';
import DefaultTextarea from '../../components/forms/defaulttextarea/DefaultTextarea';
import LoadingButton from '../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../components/page/InterventionPage';
import Signature from '../../components/signature/Signature';
import { IInstallationRouteProps } from '../../interfaces/IRoute';
import {
    accessTokenAtom,
    jobIdAtom,
    jobsListResetAtom,
    photosWithStateAtom,
} from '../../store/Atoms';
import {
    closeInterventionRequest,
    deleteAllRoomsRequest,
    docRequest,
    trackActionsRequest,
    uploadImageRequest,
} from '../../store/Requests';
import { ImageCategories } from '../../types/ImageInfo';
import { ROOT_URL } from '../../utils/constants';
import { getErrorMessage } from '../../utils/errorMessage';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';

type ConfirmFormValues = {
    installerComment: string;
    customerComment: string;
};

const ConfirmSchema = () =>
    Yup.object().shape({
        installerComment: Yup.string().required(t`Please enter comment`),
        customerComment: Yup.string(),
    });

const Confirm: React.FC<IInstallationRouteProps> = memo(() => {
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [accessToken] = useAtom(accessTokenAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [value] = useAtom(photosWithStateAtom);
    const [, resetJobsList] = useAtom(jobsListResetAtom);
    const [customerSignatureDataUri, setCustomerSignatureDataUri] =
        useState('');
    const [installerSignatureDataUri, setInstallerSignatureDataUri] =
        useState('');
    const photosList = value.state === 'hasData' ? value.data.photosList : [];
    const customerSignature = photosList.filter(
        (image: string) => !!image.match(/_SIG_/)
    )[0];
    const installerSignature = photosList.filter(
        (image: string) => !!image.match(/_ISIG_/)
    )[0];
    const hasCustomerSignature = customerSignatureDataUri || customerSignature;
    const hasInstallerSignature =
        installerSignatureDataUri || installerSignature;

    const handleFormSubmit = useCallback(
        async (
            data: ConfirmFormValues,
            actions: FormikHelpers<ConfirmFormValues>
        ) => {
            actions.setSubmitting(true);
            try {
                const promises = [
                    docRequest(accessToken, jobId, {
                        installerComment: data.installerComment,
                        customerComment: data.customerComment,
                    }),
                ];

                if (installerSignatureDataUri) {
                    promises.push(
                        uploadImageRequest(accessToken, jobId, {
                            typepic: ImageCategories.installerSignature,
                            typefic: 'jpg',
                            dataUri: installerSignatureDataUri,
                        })
                    );
                }

                if (customerSignatureDataUri) {
                    promises.push(
                        uploadImageRequest(accessToken, jobId, {
                            typepic: ImageCategories.customerSignature,
                            typefic: 'jpg',
                            dataUri: customerSignatureDataUri,
                        })
                    );
                }

                await Promise.all(promises);
                await deleteAllRoomsRequest(accessToken, jobId);
                await closeInterventionRequest();
                trackActionsRequest('complete_intervention');
                router.push('/?interventionCompleted=true', 'back');
                resetJobsList();
            } catch (e: any) {
                await presentToast({
                    message: getErrorMessage(
                        e,
                        t`Oops, something went wrong while closing uninstallation`
                    ),
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [
            accessToken,
            customerSignatureDataUri,
            installerSignatureDataUri,
            jobId,
            presentToast,
            resetJobsList,
            router,
        ]
    );

    return (
        <InterventionPage title={t`Confirm`} withPadding>
            <LoadableContent value={value}>
                <Formik
                    initialValues={{
                        installerComment: '',
                        customerComment: '',
                    }}
                    validationSchema={ConfirmSchema()}
                    validateOnMount={true}
                    onSubmit={handleFormSubmit}
                >
                    {({
                        values,
                        handleChange,
                        isSubmitting,
                        isValid,
                        errors,
                        touched,
                    }) => (
                        <Form>
                            <DefaultTextarea
                                className={classNames({
                                    'ion-invalid': errors.installerComment,
                                    'ion-touched': touched.installerComment,
                                })}
                                name="installerComment"
                                value={values.installerComment}
                                label={t`Installer comment`}
                                onIonInput={handleChange}
                                errorText={errors.installerComment}
                            ></DefaultTextarea>
                            <h3>
                                <Trans>
                                    This part is to be filled by the customer.
                                </Trans>
                            </h3>
                            <div>
                                <Trans>
                                    Please ask the customer to confirm the
                                    uninstallation, comment if necessary and
                                    sign before closing the uninstallation.
                                </Trans>
                            </div>
                            <DefaultTextarea
                                className="ion-margin-top"
                                name="customerComment"
                                value={values.customerComment}
                                label={t`Customer comment`}
                                onIonInput={handleChange}
                            ></DefaultTextarea>
                            <Signature
                                className="ion-margin-top"
                                onChange={setCustomerSignatureDataUri}
                                label={t`Customer signature`}
                                signatureImageUrl={
                                    customerSignature &&
                                    `${ROOT_URL}${customerSignature}?jwt=${accessToken}`
                                }
                            />
                            <Signature
                                className="ion-margin-top"
                                onChange={setInstallerSignatureDataUri}
                                label={t`Installer signature`}
                                signatureImageUrl={
                                    installerSignature &&
                                    `${ROOT_URL}${installerSignature}?jwt=${accessToken}`
                                }
                            />
                            <LoadingButton
                                className="ion-margin-top"
                                disabled={
                                    !isValid ||
                                    !hasCustomerSignature ||
                                    !hasInstallerSignature
                                }
                                loading={isSubmitting}
                                type="submit"
                            >
                                <Trans>Close Uninstallation</Trans>
                            </LoadingButton>
                        </Form>
                    )}
                </Formik>
            </LoadableContent>
        </InterventionPage>
    );
});

export default Confirm;
